import { Switch, Route, useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { useEffect, lazy, Suspense, useState, useTransition } from "react";
import { useAuth } from "@/hooks/use-auth";
import { usePushRegistration } from "@/hooks/use-push-registration";
import { useIosNotificationCleanup } from "@/hooks/use-ios-notification-cleanup";
import { PushPermissionDialog } from "@/components/PushPermissionDialog";
import { queryClient } from "./lib/queryClient";
import { SocketProvider } from "@/contexts/SocketContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/contexts/CartContext";
import { SellerConflictDialog } from "@/components/SellerConflictDialog";
import { LanguageProvider } from "@/contexts/LanguageContext";
import NotFound from "@/pages/not-found";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

const Home = lazy(() => import("@/pages/Home"));
const Footer = lazy(() => import("@/components/Footer").then(m => ({ default: m.Footer })));

const ListingDetail = lazy(() => import("@/pages/ListingDetail"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Messages = lazy(() => import("@/pages/Messages"));
const MyListings = lazy(() => import("@/pages/MyListings"));
const CreateListing = lazy(() => import("@/pages/CreateListing"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const RegisterChoice = lazy(() => import("@/pages/RegisterChoice"));
const RegisterCustomer = lazy(() => import("@/pages/RegisterCustomer"));
const RegisterWholesale = lazy(() => import("@/pages/RegisterWholesale"));
const Login = lazy(() => import("@/pages/Login"));
const OtpTest = lazy(() => import("@/pages/OtpTest"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const AccountSettings = lazy(() => import("@/pages/AccountSettings"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const TodayProducts = lazy(() => import("@/pages/TodayProducts"));
const FridayMarket = lazy(() => import("@/pages/FridayMarket"));
const WorkWithUs = lazy(() => import("@/pages/WorkWithUs"));
const WorkWithUsLanding = lazy(() => import("@/pages/WorkWithUsLanding"));
const MyInventory = lazy(() => import("@/pages/MyInventory"));
const MyResellerInventory = lazy(() => import("@/pages/MyResellerInventory"));
const Shorja = lazy(() => import("@/pages/Shorja"));
const ChinaMarket = lazy(() => import("@/pages/ChinaMarket"));
const TurkeyMarket = lazy(() => import("@/pages/TurkeyMarket"));
const IraqWholesale = lazy(() => import("@/pages/IraqWholesale"));
const BaghdadMarket = lazy(() => import("@/pages/BaghdadMarket"));
const VirtualStore = lazy(() => import("@/pages/VirtualStore"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const MyOrders = lazy(() => import("@/pages/MyOrders"));
const Contact = lazy(() => import("@/pages/Contact"));
const AdminChat = lazy(() => import("@/pages/AdminChat"));
const AllProducts = lazy(() => import("@/pages/AllProducts"));
// Mobile-only categories landing page — wired to the BottomNav "الأقسام" tab.
// Desktop visitors are auto-redirected from inside the page to /all-products,
// so the desktop UX stays exactly as it was before this step.
const MobileCategories = lazy(() => import("@/pages/MobileCategories"));
const Compare = lazy(() => import("@/pages/Compare"));
const RecentlyViewed = lazy(() => import("@/pages/RecentlyViewed"));
const MyAlerts = lazy(() => import("@/pages/MyAlerts"));
const MapView = lazy(() => import("@/pages/MapView"));
const SellerDashboard = lazy(() => import("@/pages/SellerDashboard"));
const AdvancedSearch = lazy(() => import("@/pages/AdvancedSearch"));
const DropshippingProducts = lazy(() => import("@/pages/DropshippingProducts"));
const MerchantNotifications = lazy(() => import("@/pages/MerchantNotifications"));
const VirtualShopOrders = lazy(() => import("@/pages/VirtualShopOrders"));
const MerchantDropshippingOrders = lazy(() => import("@/pages/MerchantDropshippingOrders"));
const MerchantDirectWholesaleOrders = lazy(() => import("@/pages/MerchantDirectWholesaleOrders"));
const MerchantPartnerStores = lazy(() => import("@/pages/MerchantPartnerStores"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const AdminPayoutRequests = lazy(() => import("@/pages/AdminPayoutRequests"));
const SupplierDashboard = lazy(() => import("@/pages/SupplierDashboard"));
const DeliveryConfirm = lazy(() => import("@/pages/DeliveryConfirm"));
const DeliveryLogin = lazy(() => import("@/pages/DeliveryLogin"));
const DeliveryDashboard = lazy(() => import("@/pages/DeliveryDashboard"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DeleteAccount = lazy(() => import("@/pages/DeleteAccount"));
const PhysicalStores = lazy(() => import("@/pages/PhysicalStores"));
const StoreProductDetail = lazy(() => import("@/pages/StoreProductDetail"));
const AllStores = lazy(() => import("@/pages/AllStores"));
const MyDisputes = lazy(() => import("@/pages/MyDisputes"));
const MyReturns = lazy(() => import("@/pages/MyReturns"));
const IncomingReturns = lazy(() => import("@/pages/IncomingReturns"));

const FloatingCartButton = lazy(() => import("@/components/FloatingCartButton").then(m => ({ default: m.FloatingCartButton })));
const FloatingAddButton = lazy(() => import("@/components/FloatingAddButton").then(m => ({ default: m.FloatingAddButton })));
const QuickGuideFAB = lazy(() => import("@/components/QuickGuideModal").then(m => ({ default: m.QuickGuideFAB })));
const AIChatbot = lazy(() => import("@/components/AIChatbot").then(m => ({ default: m.AIChatbot })));
const FloatingContactButton = lazy(() => import("@/components/FloatingContactButton").then(m => ({ default: m.FloatingContactButton })));

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

// Mount point for the native push token registration side-effect.
// No-op on the web — the hook short-circuits when not running on Capacitor.
// Renders nothing except the one-time permission explainer, which the hook
// opens only on native and only while the OS prompt is still unspent.
function PushRegistrationManager() {
  const { explainerOpen, onEnable, onDismiss } = usePushRegistration();
  // Also wipe the iOS app-icon badge + delivered notifications on every
  // app foreground transition. Without this the badge sticks at "1"
  // after a single push, because iOS never auto-clears badges.
  useIosNotificationCleanup();
  return (
    <PushPermissionDialog
      open={explainerOpen}
      onEnable={onEnable}
      onDismiss={onDismiss}
    />
  );
}

function PageSpinner() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

/** Native-only: blocks the chrome until /api/auth/user resolves so guests never flash logged-in UI. */
function NativeAuthBootstrapOverlay() {
  const { isInitialized } = useAuth();
  const [loc] = useLocation();
  if (!Capacitor.isNativePlatform() || isInitialized) return null;
  const onAuthPage =
    loc.startsWith("/login") ||
    loc.startsWith("/register") ||
    loc.startsWith("/forgot-password") ||
    loc.startsWith("/otp-test") ||
    loc.startsWith("/delivery-");
  if (onAuthPage) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/85 backdrop-blur-sm">
      <div
        className="h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"
        role="status"
        aria-label="Checking session"
      />
    </div>
  );
}

function Routes() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/category/:category" component={CategoryPage} />
      <Route path="/listing/:id" component={ListingDetail} />
      <Route path="/store-product/:id" component={StoreProductDetail} />
      <Route path="/all-stores" component={AllStores} />
      <Route path="/virtual-stores" component={AllStores} />
      <Route path="/favorites" component={Favorites} />
      <Route path="/messages" component={Messages} />
      <Route path="/my-listings" component={MyListings} />
      <Route path="/create-listing" component={CreateListing} />
      <Route path="/register" component={RegisterChoice} />
      <Route path="/register/customer" component={RegisterCustomer} />
      <Route path="/register/wholesale" component={RegisterWholesale} />
      <Route path="/login" component={Login} />
      <Route path="/otp-test" component={OtpTest} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/settings" component={AccountSettings} />
      <Route path="/admin" component={AdminPanel} />
      <Route path="/today" component={TodayProducts} />
      <Route path="/today-products" component={TodayProducts} />
      <Route path="/friday" component={FridayMarket} />
      <Route path="/friday-market" component={FridayMarket} />
      <Route path="/work-with-us" component={WorkWithUs} />
      <Route path="/work-with-us-info" component={WorkWithUsLanding} />
      <Route path="/my-inventory" component={MyInventory} />
      <Route path="/my-reseller-inventory" component={MyResellerInventory} />
      <Route path="/shorja" component={Shorja} />
      <Route path="/china" component={ChinaMarket} />
      <Route path="/turkey" component={TurkeyMarket} />
      <Route path="/iraq-wholesale" component={IraqWholesale} />
      <Route path="/baghdad-market/:market" component={BaghdadMarket} />
      <Route path="/store/:userId" component={VirtualStore} />
      <Route path="/checkout" component={Checkout} />
      <Route path="/my-orders" component={MyOrders} />
      <Route path="/contact" component={Contact} />
      <Route path="/admin-chat" component={AdminChat} />
      <Route path="/all-products" component={AllProducts} />
      <Route path="/categories" component={MobileCategories} />
      <Route path="/compare" component={Compare} />
      <Route path="/recently-viewed" component={RecentlyViewed} />
      <Route path="/my-alerts" component={MyAlerts} />
      <Route path="/map" component={MapView} />
      <Route path="/seller-dashboard" component={SellerDashboard} />
      <Route path="/search" component={AdvancedSearch} />
      <Route path="/advanced-search" component={AdvancedSearch} />
      <Route path="/dropshipping" component={DropshippingProducts} />
      <Route path="/merchant-notifications" component={MerchantNotifications} />
      <Route path="/virtual-shop/orders" component={VirtualShopOrders} />
      <Route path="/merchant/dropshipping-orders" component={MerchantDropshippingOrders} />
      <Route path="/merchant/direct-wholesale-orders" component={MerchantDirectWholesaleOrders} />
      <Route path="/merchant/partner-stores" component={MerchantPartnerStores} />
      <Route path="/wallet" component={Wallet} />
      <Route path="/admin/payout-requests" component={AdminPayoutRequests} />
      <Route path="/supplier-dashboard" component={SupplierDashboard} />
      <Route path="/delivery-confirm" component={DeliveryConfirm} />
      <Route path="/delivery-login" component={DeliveryLogin} />
      <Route path="/delivery-dashboard" component={DeliveryDashboard} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-service" component={TermsOfService} />
      <Route path="/delete-account" component={DeleteAccount} />
      <Route path="/my-disputes" component={MyDisputes} />
      <Route path="/returns/incoming" component={IncomingReturns} />
      <Route path="/returns" component={MyReturns} />
      <Route path="/stores/physical" component={PhysicalStores} />
      <Route component={NotFound} />
    </Switch>
  );
}

function Router() {
  const [location] = useLocation();
  const [isPending, startTransition] = useTransition();
  const [stableLocation, setStableLocation] = useState(location);

  useEffect(() => {
    startTransition(() => {
      setStableLocation(location);
    });
  }, [location]);

  return (
    <div style={{ opacity: isPending ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
      {/*
        Per-route error boundary. Resets whenever the location changes so a
        render crash on one route (e.g. /create-listing) cannot turn the
        entire native app into a permanent white-page error fallback —
        navigating to any other route always gets a fresh attempt. The
        top-level <Sentry.ErrorBoundary> in main.tsx remains as a deeper
        last-resort safety net for errors below this layer.
      */}
      <Suspense fallback={<PageSpinner />}>
        <RouteErrorBoundary resetKey={stableLocation}>
          <Routes />
        </RouteErrorBoundary>
      </Suspense>
    </div>
  );
}

function App() {
  const [location] = useLocation();
  const isAdminRoute = location === "/admin" || location.startsWith("/admin/");
  // Delivery confirm is a standalone page for the delivery agent — no app chrome needed
  const isDeliveryRoute = location.startsWith("/delivery-confirm");

  return (
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <SocketProvider>
          <CartProvider>
            <PushRegistrationManager />
            <SellerConflictDialog />
            <TooltipProvider>
              <NativeAuthBootstrapOverlay />
              <ScrollToTop />
              <Toaster />
              {!isAdminRoute && !isDeliveryRoute && <Header />}
              {/*
                pb-16 reserves room for the fixed BottomNav (h-16). The reset
                breakpoint is `lg:` (≥1024px) so it matches BottomNav's own
                `lg:hidden` — anything below `lg` shows the bar AND the padding;
                desktop drops both. Gated on the same chrome guard so admin /
                delivery pages that don't render the BottomNav also keep their
                original padding.
              */}
              <main className={`min-h-screen bg-background${!isAdminRoute && !isDeliveryRoute ? " pb-16 lg:pb-0" : ""}`}>
                <Router />
              </main>
              {!isAdminRoute && !isDeliveryRoute && <Suspense fallback={null}><Footer /></Suspense>}
              {!isAdminRoute && !isDeliveryRoute && (
                <Suspense fallback={null}>
                  <FloatingCartButton />
                  <FloatingAddButton />
                  <QuickGuideFAB />
                  <FloatingContactButton />
                  <AIChatbot />
                </Suspense>
              )}
              {/* Mobile-only bottom tab bar — desktop chrome (Header/Footer) is unchanged. */}
              {!isAdminRoute && !isDeliveryRoute && <BottomNav />}
            </TooltipProvider>
          </CartProvider>
        </SocketProvider>
      </QueryClientProvider>
    </LanguageProvider>
  );
}

export default App;
